import React, { FC, useEffect, useRef } from 'react';
import { LuvImage } from '@/components/ui/LuvImage';
import { UniversalCta } from '@/components';
import {
  getMediaComponent,
  isVideo,
} from '@/components/common/LandingPageHero/utils';
import classNames from 'classnames';
import {
  TypeHomepageHeroFields,
  TypeImage,
  TypeKeyValueListFields,
} from '@/core/generated-types';
import './hero.scss';
import { ParallaxComponent } from './ParallaxComponent';
import { Entry } from 'contentful';
import { generateTranslationsObject } from '@/utils/translations';
import { homepageRedesignTranslations } from '../types';
import { useIsDesktopWithResize } from '@/hooks/useIsDesktopWithResize';

export const RedesignHomepageHero: FC<
  TypeHomepageHeroFields & {
    isHeroVisible: boolean;
    t: Entry<Record<string, any>>;
  }
> = ({
  entryName,
  desktopMedia,
  mobileMedia,
  subtext = '',
  headerText = '',
  cta,
  secondaryCTA,
  desktopCtaAlignment,
  isHeroVisible,
  t,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  let altText = '';
  const experimentTranslations = generateTranslationsObject(
    t.fields as TypeKeyValueListFields,
  ) as homepageRedesignTranslations;

  let typeImage: TypeImage | undefined;
  const isDesktop = useIsDesktopWithResize();
  const mobileMediaIsVideo = isVideo(mobileMedia);
  if (!mobileMediaIsVideo) {
    typeImage = mobileMedia as TypeImage;
    altText = typeImage?.fields.altText ?? '';
  }

  headerText = isDesktop
    ? experimentTranslations.heading_desktop
    : experimentTranslations.heading_mobile;

  //  Due performance, video will be paused if hero is not visible
  useEffect(() => {
    if (isHeroVisible) {
      videoRef.current && videoRef.current.play?.();
    } else {
      videoRef.current && videoRef.current.pause?.();
    }
  }, [isHeroVisible]);

  return (
    <section
      data-testid={`homepage-hero-${entryName}`}
      className="text-center lg:text-left lg:max-h-[81vh] lg:h-[51vw]"
    >
      <div className="relative lg:h-[81vh]">
        <div
          className="video-container hidden lg:block"
          /* On desktop, we want the bg video to be fixed -to have a proper parallax effect-
            but this could hurt performance, because it would stay -unnecessary- hide behind all other elements even if I scroll to the bottom.
            A workaround is to update it from `fixed` to `absolute` when the hero is not visible. */
          style={{ position: isHeroVisible ? 'fixed' : 'absolute' }}
        >
          {getMediaComponent(desktopMedia, '', true, videoRef)}
        </div>
        <div className="mx-auto max-w-[1400px] pl-0 lg:pl-6 lg:relative lg:flex lg:items-center lg:h-[60vh] lg:max-h-[36vw]">
          <div className="block lg:hidden">
            {mobileMediaIsVideo ? (
              <div>
                {/*aspect-video 16:9*/}
                {getMediaComponent(
                  mobileMedia,
                  'min-h-[211px] w-full',
                  true,
                  videoRef,
                )}
              </div>
            ) : (
              <LuvImage
                imageData={typeImage}
                alt={altText}
                height={648}
                width={750}
              />
            )}
          </div>
          {/* Text should behave as parallax, just in desktop */}
          <ParallaxComponent disable={!isDesktop}>
            <div className="relative text-grey-dark pt-3 pb-9 px-1 bg-off-white lg:bg-transparent lg:p-0 z-10 lg:!max-w-[720px] lg:text-white">
              <h1
                className="text-[36px] font-bold leading-10 px-5 lg:text-[65px] lg:px-0 lg:leading-[70px]"
                dangerouslySetInnerHTML={{ __html: headerText }}
              />
              {subtext && (
                <h4
                  className="text-d-base mt-2 font-bold lg:text-3xl px-10 lg:px-0 lg:max-w-[700px] lg:mt-7 lg:mb-5"
                  dangerouslySetInnerHTML={{
                    __html: experimentTranslations.subheading_experiment,
                  }}
                />
              )}
              <div
                className={classNames(
                  'flex flex-col lg:inline-flex',
                  desktopCtaAlignment === 'row' ? 'gap-4 lg:flex-row' : 'gap-3',
                  secondaryCTA && desktopCtaAlignment === 'row'
                    ? 'lg:w-[100%]'
                    : '',
                )}
              >
                {cta && (
                  <div className="px-6 lg:px-0">
                    <UniversalCta
                      link={cta}
                      defaultButtonStyle="Primary"
                      className={classNames('mt-4 h-14 lg:h-[62px]', {
                        'lg:grow lg:basis-0': secondaryCTA,
                      })}
                    />
                  </div>
                )}
                {secondaryCTA && (
                  <UniversalCta
                    link={secondaryCTA}
                    defaultButtonStyle="Secondary"
                    className="lg:grow lg:basis-0"
                  />
                )}
              </div>
            </div>
          </ParallaxComponent>
        </div>
      </div>
    </section>
  );
};
